import { Injectable } from '@angular/core';

import { ChatSuggestionEntry, ServiceType, CHAT_SUGGESTIONS, SERVICE_TYPES, SYNONYM_GROUPS } from 'src/shared/constants/chat-suggestions';

export interface ChatSuggestionResult extends ChatSuggestionEntry {
  score: number;
  displayText?: string;
}

interface ProcessedEntry {
  entry: ChatSuggestionEntry;
  tokens: string[];
  lowerText: string;
}

interface VirtualPhrase {
  fullText: string;
  lowerFullText: string;
  actionText: string;
  category: string;
  icon: string;
  rentalOnly?: boolean;
  isBase: boolean;
  serviceRank?: number; // Position in SERVICE_TYPES (0 = cleaning = highest priority)
}

@Injectable({ providedIn: 'root' })
export class ChatAutosuggestService {
  private static readonly STOP_WORDS = new Set([
    'a','an','the','i','me','my','we','our','you','your','he','she','it','its',
    'they','them','their','to','for','of','in','on','at','by','with','from',
    'into','out','about','over','through','and','or','but','so','if','is','am',
    'are','was','were','be','been','do','does','did','have','has','had','can',
    'could','would','should','will','shall','may','might','must','just','also',
    'too','very','really','some','any','all','not','no','yes','please','thanks',
    'thank','want','need','like','let','got',
  ]);

  private static readonly STARTER_PHRASES: string[] = [
    // Longer phrases first so prefix detection is greedy
    'i would like to', "i'd like to", 'i would love to', "i'd love to",
    'can you help me to', 'can you help me', 'could you please', 'could you help me to', 'could you',
    'please help me to', 'please help me',
    'how can i', 'how do i', 'how do i go about',
    "what's the best way to", 'what is the best way to',
    'is there a way to',
    'show me how to',
    'help me to', 'help me',
    'i want to', 'i need to',
    "i'm looking to", 'i am looking to',
    "i'm trying to", 'i am trying to',
    "i'm hoping to", 'i am hoping to',
    "i was hoping to",
    "i'm interested in", 'i am interested in',
    'can i', 'can you', 'please',
    'i want', 'i need', 'let me',
  ];

  private static readonly STARTER_PHRASES_DISPLAY = new Map<string, string>([
    ['i would like to', 'I would like to'],
    ["i'd like to", "I'd like to"],
    ['i would love to', 'I would love to'],
    ["i'd love to", "I'd love to"],
    ['can you help me to', 'Can you help me to'],
    ['can you help me', 'Can you help me'],
    ['could you please', 'Could you please'],
    ['could you help me to', 'Could you help me to'],
    ['could you', 'Could you'],
    ['please help me to', 'Please help me to'],
    ['please help me', 'Please help me'],
    ['how can i', 'How can I'],
    ['how do i', 'How do I'],
    ['how do i go about', 'How do I go about'],
    ["what's the best way to", "What's the best way to"],
    ['what is the best way to', 'What is the best way to'],
    ['is there a way to', 'Is there a way to'],
    ['show me how to', 'Show me how to'],
    ['help me to', 'Help me to'],
    ['help me', 'Help me'],
    ['i want to', 'I want to'],
    ['i need to', 'I need to'],
    ["i'm looking to", "I'm looking to"],
    ['i am looking to', 'I am looking to'],
    ["i'm trying to", "I'm trying to"],
    ['i am trying to', 'I am trying to'],
    ["i'm hoping to", "I'm hoping to"],
    ['i am hoping to', 'I am hoping to'],
    ["i was hoping to", "I was hoping to"],
    ["i'm interested in", "I'm interested in"],
    ['i am interested in', 'I am interested in'],
    ['can i', 'Can I'],
    ['can you', 'Can you'],
    ['please', 'Please'],
    ['i want', 'I want'],
    ['i need', 'I need'],
    ['let me', 'Let me'],
  ]);

  private static readonly QUESTION_STARTERS = new Set([
    'how', 'what', 'where', 'when', 'why', 'who',
  ]);

  // Categories that represent bookable/schedulable actions — ranked above management
  private static readonly BOOKING_CATEGORIES = new Set([
    'cleaning', 'maintenance', 'scheduling',
  ]);

  private static readonly POPULAR_DEFAULTS = new Set([
    'schedule a cleaning', 'book a cleaning', 'find a pro', 'view billing',
    'check my schedule', 'request maintenance', 'add a checklist item',
    'schedule plumbing', 'get quotes for cleaning',
    'report an issue', 'order supplies', 'check availability',
  ]);

  private static readonly VERB_TEMPLATES: [string, string][] = [
    ['Get quotes for', 'Let me help you get quotes for {s}...'],
    ['Schedule', 'Let me help you schedule {s}...'],
    ['Book', 'Let me help you book {s}...'],
    ['Order', 'Let me help you order {s}...'],
    ['Find', 'Let me help you find {s}...'],
    ['Request', 'Looking into {s} for you...'],
    ['Report', 'Looking into {s} for you...'],
    ['View', 'Let me check {s}...'],
    ['See', 'Let me check {s}...'],
    ['Check', 'Let me check {s}...'],
    ['Download', 'Let me help you download {s}...'],
    ['Dispute', 'Let me help you with {s}...'],
    ['Apply', 'Let me help you apply {s}...'],
    ['Cancel', 'Let me help you cancel {s}...'],
    ['Reschedule', 'Let me help you reschedule {s}...'],
    ['Pause', 'Let me help you pause {s}...'],
    ['Resume', 'Let me help you resume {s}...'],
    ['Add', 'Let me help you add {s}...'],
    ['Create', 'Let me help you create {s}...'],
    ['Update', 'Let me help you update {s}...'],
    ['Change', 'Let me help you change {s}...'],
    ['Modify', 'Let me help you modify {s}...'],
    ['Set up', 'Let me help you set up {s}...'],
    ['Configure', 'Let me help you configure {s}...'],
    ['Adjust', 'Let me help you adjust {s}...'],
    ['Log', 'Let me help you log {s}...'],
    ['Remove', 'Let me help you remove {s}...'],
    ['Connect', 'Let me help you connect {s}...'],
    ['Message', 'Let me help you message {s}...'],
    ['Contact', 'Let me connect you with {s}...'],
    ['Talk', 'Let me connect you with {s}...'],
    ['Rate', 'Let me help you rate {s}...'],
    ['Tip', 'Let me help you tip {s}...'],
    ['Refer', 'Let me help you refer {s}...'],
  ];

  private readonly synonymLookup: Map<string, Set<string>>;
  private readonly processedEntries: ProcessedEntry[];
  private readonly virtualPhrases: VirtualPhrase[];

  constructor() {
    this.synonymLookup = this.buildSynonymLookup(SYNONYM_GROUPS);
    this.processedEntries = this.preprocessEntries(CHAT_SUGGESTIONS);
    this.virtualPhrases = this.buildVirtualPhrases(CHAT_SUGGESTIONS);
  }

  search(input: string, maxResults = 6, isRentalClient = false): ChatSuggestionResult[] {
    const trimmed = input?.trim();
    if (!trimmed || trimmed.length < 2) {
      return [];
    }

    const lowerInput = trimmed.toLowerCase();

    // Prefix matching only: if typed text no longer matches the start of any suggestion, show nothing.
    return this.searchPrefix(lowerInput, trimmed, maxResults, isRentalClient);
  }

  getThinkingPhrase(input: string, isRentalClient = false): string {
    const lowerInput = input?.trim()?.toLowerCase() || '';
    for (const phrase of ChatAutosuggestService.STARTER_PHRASES) {
      if (lowerInput === phrase) {
        return 'Let me look into that...';
      }
    }

    const results = this.search(input, 1, isRentalClient);
    if (results.length === 0 || results[0].score < 3) {
      return 'Let me look into that...';
    }

    const text = results[0].text;

    for (const [verb, template] of ChatAutosuggestService.VERB_TEMPLATES) {
      if (text.startsWith(verb)) {
        const subject = text.substring(verb.length + 1).toLowerCase().replace(/\bmy\b/g, 'your');
        if (subject) {
          return template.replace('{s}', subject);
        }
        break;
      }
    }

    if (text.startsWith('How')) {
      return 'Let me explain that for you...';
    }

    return 'Let me help you with that...';
  }

  private searchPrefix(
    lowerInput: string,
    originalInput: string,
    maxResults: number,
    isRentalClient: boolean,
  ): ChatSuggestionResult[] {
    const candidates: { phrase: VirtualPhrase; score: number }[] = [];

    for (const phrase of this.virtualPhrases) {
      if (phrase.rentalOnly && !isRentalClient) {
        continue;
      }
      if (!phrase.lowerFullText.startsWith(lowerInput)) {
        continue;
      }

      let score = 0;

      if (lowerInput === phrase.lowerFullText) {
        score = 100;
      } else {
        const completionRatio = lowerInput.length / phrase.lowerFullText.length;
        score = (phrase.isBase ? 20 : 0) + completionRatio * 10;
        if (ChatAutosuggestService.POPULAR_DEFAULTS.has(phrase.actionText.toLowerCase())) {
          score += 5;
        }
        // Boost booking/scheduling categories so they rank above management actions
        if (ChatAutosuggestService.BOOKING_CATEGORIES.has(phrase.category)) {
          score += 4;
        }
      }

      candidates.push({ phrase, score });
    }

    if (candidates.length === 0) {
      return [];
    }

    candidates.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      // Service-type entries: cleaning always first, then by rank in SERVICE_TYPES
      const aRank = a.phrase.serviceRank ?? Infinity;
      const bRank = b.phrase.serviceRank ?? Infinity;
      if (aRank !== bRank) {
        return aRank - bRank;
      }
      if (a.phrase.fullText.length !== b.phrase.fullText.length) {
        return a.phrase.fullText.length - b.phrase.fullText.length;
      }
      return a.phrase.fullText.localeCompare(b.phrase.fullText);
    });

    const maxPerCategory = 3;
    const categoryCounts = new Map<string, number>();
    const diversified: ChatSuggestionResult[] = [];

    for (const candidate of candidates) {
      const { phrase, score } = candidate;
      const isExactMatch = lowerInput === phrase.lowerFullText;
      const count = categoryCounts.get(phrase.category) || 0;

      if (isExactMatch || count < maxPerCategory) {
        const displayText = phrase.isBase
          ? undefined
          : originalInput + phrase.fullText.substring(originalInput.length);

        diversified.push({
          text: phrase.actionText,
          category: phrase.category,
          keywords: [],
          icon: phrase.icon,
          rentalOnly: phrase.rentalOnly,
          score,
          displayText,
        });

        if (!isExactMatch) {
          categoryCounts.set(phrase.category, count + 1);
        }
      }

      if (diversified.length >= maxResults) {
        break;
      }
    }

    return diversified;
  }

  private searchCore(input: string, maxResults: number, isRentalClient: boolean): ChatSuggestionResult[] {
    const inputTokens = this.tokenize(input);
    const meaningfulTokens = inputTokens.filter(t => !ChatAutosuggestService.STOP_WORDS.has(t));

    if (meaningfulTokens.length === 0) {
      return [];
    }

    const expandedTokenSets = meaningfulTokens.map(token => this.expandToken(token));
    const minScore = meaningfulTokens.length <= 1 ? 0.5 : meaningfulTokens.length * 1.5;

    const results: ChatSuggestionResult[] = [];

    for (const processed of this.processedEntries) {
      if (processed.entry.rentalOnly && !isRentalClient) {
        continue;
      }

      const score = this.scoreEntry(expandedTokenSets, processed, input);
      if (score >= minScore) {
        results.push({ ...processed.entry, score });
      }
    }

    results.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      if (a.text.length !== b.text.length) {
        return a.text.length - b.text.length;
      }
      return a.text.localeCompare(b.text);
    });

    const maxPerCategory = 1;
    const categoryCounts = new Map<string, number>();
    const diversified: ChatSuggestionResult[] = [];

    for (const result of results) {
      const count = categoryCounts.get(result.category) || 0;
      if (count < maxPerCategory) {
        diversified.push(result);
        categoryCounts.set(result.category, count + 1);
      }
      if (diversified.length >= maxResults) {
        break;
      }
    }

    return diversified;
  }

  private buildVirtualPhrases(entries: ChatSuggestionEntry[]): VirtualPhrase[] {
    const phrases: VirtualPhrase[] = [];

    for (const entry of entries) {
      phrases.push({
        fullText: entry.text,
        lowerFullText: entry.text.toLowerCase(),
        actionText: entry.text,
        category: entry.category,
        icon: entry.icon,
        rentalOnly: entry.rentalOnly,
        isBase: true,
      });

      const firstWord = entry.text.split(/\s+/)[0].toLowerCase();
      if (ChatAutosuggestService.QUESTION_STARTERS.has(firstWord)) {
        continue;
      }

      const lowerAction = this.lowercaseFirstChar(entry.text);
      for (const [lowerStarter, displayStarter] of ChatAutosuggestService.STARTER_PHRASES_DISPLAY) {
        const fullText = `${displayStarter  } ${  lowerAction}`;
        phrases.push({
          fullText,
          lowerFullText: fullText.toLowerCase(),
          actionText: entry.text,
          category: entry.category,
          icon: entry.icon,
          rentalOnly: entry.rentalOnly,
          isBase: false,
        });
      }
    }

    phrases.push(...this.buildServiceTypePhrases());

    return phrases;
  }

  private buildServiceTypePhrases(): VirtualPhrase[] {
    const phrases: VirtualPhrase[] = [];

    const templates: Array<{ makeAction: (svc: ServiceType) => string | null }> = [
      { makeAction: (svc) => svc.bookLabel ? `Book ${svc.bookLabel}` : null },
      { makeAction: (svc) => `Schedule ${svc.scheduleLabel}` },
      { makeAction: (svc) => `Get quotes for ${svc.name}` },
    ];

    for (const template of templates) {
      for (let rank = 0; rank < SERVICE_TYPES.length; rank++) {
        const svc = SERVICE_TYPES[rank];
        const actionText = template.makeAction(svc);
        if (!actionText) {
          continue;
        }

        phrases.push({
          fullText: actionText,
          lowerFullText: actionText.toLowerCase(),
          actionText,
          category: svc.category,
          icon: svc.icon,
          isBase: true,
          serviceRank: rank,
        });

        const lowerAction = this.lowercaseFirstChar(actionText);
        for (const [, displayStarter] of ChatAutosuggestService.STARTER_PHRASES_DISPLAY) {
          const fullText = `${displayStarter  } ${  lowerAction}`;
          phrases.push({
            fullText,
            lowerFullText: fullText.toLowerCase(),
            actionText,
            category: svc.category,
            icon: svc.icon,
            isBase: false,
            serviceRank: rank,
          });
        }
      }
    }

    return phrases;
  }

  private detectStarterPhrase(lowerInput: string): { phrase: string; remainder: string } | null {
    for (const phrase of ChatAutosuggestService.STARTER_PHRASES) {
      if (lowerInput.startsWith(phrase)) {
        const remainder = lowerInput.substring(phrase.length).trim();
        return { phrase, remainder };
      }
    }
    return null;
  }

  private buildDisplayText(prefix: string, entryText: string): string | undefined {
    const firstWord = entryText.split(/\s+/)[0].toLowerCase();
    if (ChatAutosuggestService.QUESTION_STARTERS.has(firstWord)) {
      return undefined;
    }
    return `${prefix  } ${  this.lowercaseFirstChar(entryText)}`;
  }

  private lowercaseFirstChar(text: string): string {
    if (!text) {
      return text;
    }
    return text.charAt(0).toLowerCase() + text.slice(1);
  }

  private buildSynonymLookup(groups: string[][]): Map<string, Set<string>> {
    const lookup = new Map<string, Set<string>>();
    for (const group of groups) {
      const lowerGroup = group.map(w => w.toLowerCase());
      const groupSet = new Set(lowerGroup);
      for (const word of lowerGroup) {
        const existing = lookup.get(word);
        if (existing) {
          for (const w of groupSet) {
            existing.add(w);
          }
        } else {
          lookup.set(word, new Set(groupSet));
        }
      }
    }
    return lookup;
  }

  private preprocessEntries(entries: ChatSuggestionEntry[]): ProcessedEntry[] {
    return entries.map(entry => {
      const textTokens = this.tokenize(entry.text.toLowerCase());
      const keywordTokens = entry.keywords.flatMap(k => this.tokenize(k.toLowerCase()));
      const tokens = [...new Set([...textTokens, ...keywordTokens])];
      return {
        entry,
        tokens,
        lowerText: entry.text.toLowerCase(),
      };
    });
  }

  private tokenize(text: string): string[] {
    return text.split(/[\s\-/]+/).filter(t => t.length > 0);
  }

  private expandToken(token: string): Set<string> {
    const expanded = new Set<string>([token]);
    const synonyms = this.synonymLookup.get(token);
    if (synonyms) {
      for (const syn of synonyms) {
        expanded.add(syn);
      }
    }
    for (const [key, group] of this.synonymLookup) {
      if (key.startsWith(token) && token.length >= 2) {
        for (const syn of group) {
          expanded.add(syn);
        }
      }
    }
    return expanded;
  }

  private scoreEntry(expandedTokenSets: Set<string>[], processed: ProcessedEntry, rawInput: string): number {
    let score = 0;

    for (const expandedSet of expandedTokenSets) {
      let tokenMatched = false;
      let bestTokenScore = 0;
      for (const entryToken of processed.tokens) {
        for (const expanded of expandedSet) {
          if (entryToken === expanded) {
            bestTokenScore = Math.max(bestTokenScore, 3);
            tokenMatched = true;
          } else if (entryToken.startsWith(expanded) || expanded.startsWith(entryToken)) {
            bestTokenScore = Math.max(bestTokenScore, 1);
            tokenMatched = true;
          }
        }
        if (!tokenMatched && !tokenMatched) {
          for (const expanded of expandedSet) {
            if (expanded.length >= 3 && this.isFuzzyMatch(expanded, entryToken)) {
              bestTokenScore = Math.max(bestTokenScore, 0.5);
              tokenMatched = true;
              break;
            }
          }
        }
      }
      score += bestTokenScore;
    }

    const strippedInput = this.stripLeadingStopWords(rawInput);

    if (processed.lowerText.startsWith(strippedInput) && strippedInput.length > 0) {
      score += 5;
    } else if (processed.lowerText.startsWith(rawInput)) {
      score += 5;
    } else if (processed.lowerText.includes(strippedInput) && strippedInput.length > 0) {
      score += 2;
    } else if (processed.lowerText.includes(rawInput)) {
      score += 2;
    }

    return score;
  }

  private stripLeadingStopWords(input: string): string {
    const tokens = input.split(/\s+/);
    let start = 0;
    while (start < tokens.length && ChatAutosuggestService.STOP_WORDS.has(tokens[start])) {
      start++;
    }
    return tokens.slice(start).join(' ');
  }

  private isFuzzyMatch(input: string, target: string): boolean {
    if (Math.abs(input.length - target.length) > 2) {
      return false;
    }
    const maxDist = input.length <= 4 ? 1 : 2;
    const targetPrefix = target.substring(0, input.length + maxDist);
    return this.editDistance(input, targetPrefix) <= maxDist;
  }

  private editDistance(a: string, b: string): number {
    if (a.length === 0) {return b.length;}
    if (b.length === 0) {return a.length;}
    const matrix: number[][] = [];
    for (let i = 0; i <= a.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= b.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }
    return matrix[a.length][b.length];
  }
}
